export type ShapeId =
  | 'rect'
  | 'rounded-rect'
  | 'circle'
  | 'ellipse'
  | 'triangle'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'star5'
  | 'star6'
  | 'arrow'
  | 'double-arrow'
  | 'line'
  | 'heart'
  | 'speech-bubble';

export interface ShapeDefinition {
  id: ShapeId;
  label: string;
  icon: string;
  defaultWidth: number;
  defaultHeight: number;
}

export interface ShapeParams {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
}

export interface ShapeDesignItem {
  id: string;
  type: 'svg';
  top: number;
  left: number;
  width: number;
  height: number;
  angle: number;
  zIndex: number;
  src: string;
  svg: string;
  shapeSourceSvg: string;
  shapeId: ShapeId;
  shapeParams: ShapeParams;
  change_color: boolean;
  svgColors?: Record<string, string>;
}

export const SHAPE_DEFINITIONS: ShapeDefinition[] = [
  { id: 'rect', label: 'Rectangle', icon: 'M4 6h16v12H4z', defaultWidth: 150, defaultHeight: 100 },
  { id: 'rounded-rect', label: 'Rounded', icon: 'M7 6h10a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3z', defaultWidth: 150, defaultHeight: 100 },
  { id: 'circle', label: 'Circle', icon: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16z', defaultWidth: 120, defaultHeight: 120 },
  { id: 'ellipse', label: 'Ellipse', icon: 'M4 12a8 5 0 1 0 16 0 8 5 0 0 0-16 0z', defaultWidth: 160, defaultHeight: 100 },
  { id: 'triangle', label: 'Triangle', icon: 'M12 4l8 16H4z', defaultWidth: 130, defaultHeight: 120 },
  { id: 'diamond', label: 'Diamond', icon: 'M12 3l9 9-9 9-9-9z', defaultWidth: 125, defaultHeight: 125 },
  { id: 'pentagon', label: 'Pentagon', icon: 'M12 3l8 6-3 11H7L4 9z', defaultWidth: 130, defaultHeight: 125 },
  { id: 'hexagon', label: 'Hexagon', icon: 'M8 4h8l5 8-5 8H8l-5-8z', defaultWidth: 145, defaultHeight: 120 },
  { id: 'star5', label: 'Star 5', icon: 'M12 3l2.7 5.8 6.3.8-4.7 4.3 1.2 6.1-5.5-3.1L6.5 20l1.2-6.1L3 9.6l6.3-.8z', defaultWidth: 130, defaultHeight: 130 },
  { id: 'star6', label: 'Star 6', icon: 'M12 2 14.25 8.1 20.66 7 16.5 12 20.66 17 14.25 15.9 12 22 9.75 15.9 3.34 17 7.5 12 3.34 7 9.75 8.1Z', defaultWidth: 130, defaultHeight: 130 },
  { id: 'arrow', label: 'Arrow', icon: 'M4 10h10V6l6 6-6 6v-4H4z', defaultWidth: 170, defaultHeight: 90 },
  { id: 'double-arrow', label: 'Double Arrow', icon: 'M4 12l5-5v3h6V7l5 5-5 5v-3H9v3z', defaultWidth: 180, defaultHeight: 90 },
  { id: 'line', label: 'Line', icon: 'M4 12h16', defaultWidth: 170, defaultHeight: 20 },
  { id: 'heart', label: 'Heart', icon: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.7A4 4 0 0 1 19 10c0 5.6-7 10-7 10z', defaultWidth: 130, defaultHeight: 120 },
  { id: 'speech-bubble', label: 'Bubble', icon: 'M5 5h14v10H9l-4 4V5z', defaultWidth: 160, defaultHeight: 110 },
];

const DEFAULT_FILL = '#111111';
const DEFAULT_STROKE = '#000000';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function normalizeHex(value: unknown, fallback: string): string {
  const raw = String(value ?? '').trim();
  const short = /^#([0-9a-f]{3})$/i.exec(raw);
  if (short) {
    const [r, g, b] = short[1]!.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const full = /^#([0-9a-f]{6})$/i.exec(raw);
  return full ? `#${full[1]!.toLowerCase()}` : fallback;
}

export function normalizeShapeParams(params: ShapeParams = {}): Required<ShapeParams> {
  return {
    fill: normalizeHex(params.fill, DEFAULT_FILL),
    stroke: normalizeHex(params.stroke, DEFAULT_STROKE),
    strokeWidth: clamp(Number(params.strokeWidth ?? 2) || 0, 0, 24),
    cornerRadius: clamp(Number(params.cornerRadius ?? 14) || 0, 0, 48),
  };
}

function points(cx: number, cy: number, radius: number, count: number, start = -90): Array<[number, number]> {
  return Array.from({ length: count }, (_, index) => {
    const angle = ((start + (360 / count) * index) * Math.PI) / 180;
    return [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius] as [number, number];
  });
}

function starPoints(cx: number, cy: number, outer: number, inner: number, count: number): Array<[number, number]> {
  return Array.from({ length: count * 2 }, (_, index) => {
    const radius = index % 2 === 0 ? outer : inner;
    const angle = ((-90 + (180 / count) * index) * Math.PI) / 180;
    return [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius] as [number, number];
  });
}

/**
 * Path outlines in absolute commands only (M/L/H/V/C/Z), so every number can be remapped
 * into the item box without a path parser. Control points stay inside the outline's bbox,
 * which keeps the bbox of the numbers equal to the visible bbox.
 */
const PATH_OUTLINES: Partial<Record<ShapeId, string>> = {
  arrow: 'M8 38 H58 V20 L92 50 L58 80 V62 H8 Z',
  'double-arrow': 'M8 50 L35 22 V39 H65 V22 L92 50 L65 78 V61 H35 V78 Z',
  heart: 'M50 86 C34 72 16 61 16 39 C16 26 26 16 39 16 C44 16 48 19 50 23 C52 19 56 16 61 16 C74 16 84 26 84 39 C84 61 66 72 50 86 Z',
  'speech-bubble': 'M13 18 H87 V70 H39 L21 86 V70 H13 Z',
};

function polygonOutline(shapeId: ShapeId): Array<[number, number]> | null {
  switch (shapeId) {
    case 'triangle': return [[50, 10], [90, 88], [10, 88]];
    case 'diamond': return [[50, 8], [92, 50], [50, 92], [8, 50]];
    case 'pentagon': return points(50, 52, 42, 5);
    case 'hexagon': return points(50, 50, 42, 6, 30);
    case 'star5': return starPoints(50, 52, 43, 18, 5);
    case 'star6': return starPoints(50, 52, 43, 24, 6);
    default: return null;
  }
}

type Box = { w: number; h: number; inset: number };

const fmt = (value: number): string => String(Math.round(value * 100) / 100);

/** Maps a coordinate from a shape's own bbox onto the item box, inset by half the stroke. */
function mapper(box: Box, minX: number, minY: number, spanX: number, spanY: number) {
  const innerW = Math.max(box.w - box.inset * 2, 0.01);
  const innerH = Math.max(box.h - box.inset * 2, 0.01);
  return {
    x: (value: number) => box.inset + ((value - minX) / (spanX || 1)) * innerW,
    y: (value: number) => box.inset + ((value - minY) / (spanY || 1)) * innerH,
  };
}

function fitPolygon(outline: Array<[number, number]>, box: Box): string {
  const xs = outline.map(([x]) => x);
  const ys = outline.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const map = mapper(box, minX, minY, Math.max(...xs) - minX, Math.max(...ys) - minY);
  return outline.map(([x, y]) => `${fmt(map.x(x))},${fmt(map.y(y))}`).join(' ');
}

function fitPath(outline: string, box: Box): string {
  const tokens = outline.match(/[MLHVCZ]|-?\d*\.?\d+/g) ?? [];
  // First pass: collect the bbox, tracking which axis each number belongs to.
  const xs: number[] = [];
  const ys: number[] = [];
  const walk = (visit: (value: number, axis: 'x' | 'y') => string): string => {
    let command = '';
    let pairIndex = 0;
    return tokens.map((token) => {
      if (/^[A-Z]$/.test(token)) {
        command = token;
        pairIndex = 0;
        return token;
      }
      const value = Number(token);
      const axis: 'x' | 'y' = command === 'H' ? 'x' : command === 'V' ? 'y' : (pairIndex++ % 2 === 0 ? 'x' : 'y');
      return visit(value, axis);
    }).join(' ');
  };
  walk((value, axis) => {
    (axis === 'x' ? xs : ys).push(value);
    return '';
  });
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const map = mapper(box, minX, minY, Math.max(...xs) - minX, Math.max(...ys) - minY);
  return walk((value, axis) => fmt(axis === 'x' ? map.x(value) : map.y(value)));
}

function body(shapeId: ShapeId, params: Required<ShapeParams>, box: Box): string {
  const fill = shapeId === 'line' ? 'none' : params.fill;
  const common = `fill="${fill}" stroke="${params.stroke}" stroke-width="${fmt(params.strokeWidth)}" stroke-linejoin="round" stroke-linecap="round"`;
  const { w, h, inset } = box;
  const innerW = Math.max(w - inset * 2, 0.01);
  const innerH = Math.max(h - inset * 2, 0.01);
  switch (shapeId) {
    case 'rect':
      return `<rect x="${fmt(inset)}" y="${fmt(inset)}" width="${fmt(innerW)}" height="${fmt(innerH)}" ${common}/>`;
    case 'rounded-rect': {
      const radius = fmt(Math.min(params.cornerRadius, innerW / 2, innerH / 2));
      return `<rect x="${fmt(inset)}" y="${fmt(inset)}" width="${fmt(innerW)}" height="${fmt(innerH)}" rx="${radius}" ry="${radius}" ${common}/>`;
    }
    case 'circle':
    case 'ellipse':
      return `<ellipse cx="${fmt(w / 2)}" cy="${fmt(h / 2)}" rx="${fmt(innerW / 2)}" ry="${fmt(innerH / 2)}" ${common}/>`;
    case 'line':
      return `<path d="M${fmt(inset)} ${fmt(h / 2)}H${fmt(w - inset)}" ${common}/>`;
  }
  const polygon = polygonOutline(shapeId);
  if (polygon) return `<polygon points="${fitPolygon(polygon, box)}" ${common}/>`;
  return `<path d="${fitPath(PATH_OUTLINES[shapeId] ?? '', box)}" ${common}/>`;
}

/**
 * Draws the shape edge-to-edge in a `w × h` box (the item's own size), so the selection box
 * hugs the outline. The stroke is inset by half its width so it is never clipped, and stays
 * in item pixels so stretching the box does not distort it. `preserveAspectRatio="none"`
 * only matters mid-drag, before the item is redrawn at its new size.
 */
export function generateShapeSvg(shapeId: ShapeId, params: ShapeParams = {}, width = 100, height = 100): string {
  const normalized = normalizeShapeParams(params);
  const w = Math.max(1, Math.round(Number(width) || 100));
  const h = Math.max(1, Math.round(Number(height) || 100));
  const inset = Math.min(normalized.strokeWidth, w, h) / 2;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" preserveAspectRatio="none">${body(shapeId, normalized, { w, h, inset })}</svg>`;
}

export function applyShapeColors(source: string, colors: Record<string, string> = {}): string {
  let svg = source;
  for (const [fromRaw, toRaw] of Object.entries(colors)) {
    const from = normalizeHex(fromRaw, '');
    const to = normalizeHex(toRaw, '');
    if (!from || !to) continue;
    svg = svg.split(from).join(to).split(from.toUpperCase()).join(to);
  }
  return svg;
}

export function shapeSvgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function renderShape(
  shapeId: ShapeId,
  params: ShapeParams = {},
  colors: Record<string, string> = {},
  width?: number,
  height?: number,
) {
  const source = generateShapeSvg(shapeId, params, width, height);
  const svg = applyShapeColors(source, colors);
  return { source, svg, src: shapeSvgDataUrl(svg) };
}

export function isShapeId(value: unknown): value is ShapeId {
  return SHAPE_DEFINITIONS.some((shape) => shape.id === value);
}
